import { useEffect, useState } from 'react';
import { Snowflake, Forward, MessageCircle, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { atendimentoApi } from '../atendimento/lib/api';

export interface ChamadoPendente {
  id: number;
  inbox: string;
  contato_nome: string | null;
  ultima_mensagem_em: string | null;
  ultima_mensagem_preview: string | null;
}

export interface PendenciasSaida {
  bloqueia: boolean;
  chamados: ChamadoPendente[];
  janela: 'dentro' | 'fora' | 'sem_horario';
  janela_min: number;
  hora_fim: string | null;
  algum_em_turno: boolean;
}

interface Props {
  pendencias: PendenciasSaida;
  // Recarrega as pendencias no pai; se zerar, o pai fecha o gate e segue pro FR.
  onRecarregar: () => Promise<void>;
  onCongelarESair: () => Promise<void>;     // POST /saida/congelar-e-sair -> segue pro FR
  onEncerrarTodosESair: () => Promise<void>; // POST /saida/encerrar-pendencias -> segue pro FR
  onAbrir: (conversaId: number) => void;
  onFechar: () => void;                      // Voltar (cancela a saida)
}

type AgenteDisp = { id: number; nome: string; foto?: string | null };

function tempoAtras(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'agora';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `ha ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `ha ${h} h`;
  return `ha ${Math.floor(h / 24)} d`;
}

export function GateSaidaChamadosModal({
  pendencias, onRecarregar, onCongelarESair, onEncerrarTodosESair, onAbrir, onFechar,
}: Props) {
  const toast = useToast();
  const [trabalhando, setTrabalhando] = useState(false);          // acao em lote (congelar/encerrar todos)
  const [linhaOcupada, setLinhaOcupada] = useState<number | null>(null); // acao por linha
  const [transferindoId, setTransferindoId] = useState<number | null>(null);
  const [agentes, setAgentes] = useState<AgenteDisp[] | null>(null);

  const { chamados, janela, janela_min, hora_fim, algum_em_turno } = pendencias;
  const podeCongelar = janela === 'dentro';
  const podeEncerrarTodos = !algum_em_turno;

  // Carrega agentes em turno (pra transferencia) sob demanda na 1a abertura de uma linha.
  useEffect(() => {
    if (transferindoId == null || agentes != null) return;
    atendimentoApi.listarAgentesDisponiveis('atendimento')
      .then((r) => setAgentes(r as AgenteDisp[]))
      .catch(() => { setAgentes([]); toast.erro('Falha ao carregar atendentes em turno'); });
  }, [transferindoId, agentes, toast]);

  async function encerrarUm(id: number) {
    setLinhaOcupada(id);
    try {
      await atendimentoApi.atualizarStatusChamado(id, 'encerrado');
      await onRecarregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao encerrar');
    } finally {
      setLinhaOcupada(null);
    }
  }

  async function transferirPara(id: number, agenteId: number) {
    setLinhaOcupada(id);
    try {
      await atendimentoApi.atualizarConversa(id, { usuario_atribuido_id: agenteId });
      setTransferindoId(null);
      await onRecarregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao transferir');
    } finally {
      setLinhaOcupada(null);
    }
  }

  async function congelarTodos() {
    setTrabalhando(true);
    try { await onCongelarESair(); }
    catch (e: any) { toast.erro(e?.message ?? 'Falha ao congelar'); setTrabalhando(false); }
  }

  async function encerrarTodos() {
    setTrabalhando(true);
    try { await onEncerrarTodosESair(); }
    catch (e: any) { toast.erro(e?.message ?? 'Falha ao encerrar'); setTrabalhando(false); }
  }

  const ocupado = trabalhando || linhaOcupada != null;

  return (
    <Modal
      aberto
      onFechar={ocupado ? () => {} : onFechar}
      titulo="Antes de bater saida"
      largura={560}
      rodape={
        <div className="flex items-center justify-between w-full gap-2">
          <span className="text-xs opacity-60">
            {chamados.length} chamado{chamados.length !== 1 ? 's' : ''} em aberto
          </span>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secundario" onClick={onFechar} disabled={ocupado}>
              Voltar
            </button>
            {podeCongelar ? (
              <button
                type="button"
                className="btn-primario flex items-center gap-1.5"
                onClick={congelarTodos}
                disabled={ocupado}
              >
                {trabalhando ? <Loader2 size={14} className="animate-spin" /> : <Snowflake size={14} />}
                Congelar todos e sair
              </button>
            ) : podeEncerrarTodos ? (
              <button
                type="button"
                className="btn-primario flex items-center gap-1.5"
                onClick={encerrarTodos}
                disabled={ocupado}
              >
                {trabalhando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Encerrar todos e sair
              </button>
            ) : null}
          </div>
        </div>
      }
    >
      <div
        className="mb-3 p-3 rounded-lg text-sm leading-relaxed"
        style={{ backgroundColor: '#fde68a', color: '#000' }}
      >
        Voce tem chamados em aberto. Resolva, transfira ou{' '}
        {podeCongelar
          ? <>congele (continua amanha)</>
          : algum_em_turno
            ? <>transfira para quem esta em turno</>
            : <>encerre</>}{' '}
        antes de sair — pra nenhum cliente ficar sem resposta.
        {!podeCongelar && janela === 'fora' && (
          <div className="mt-1 text-[12px] opacity-80">
            Voce esta saindo antes dos ultimos {janela_min} min do turno
            {hora_fim ? ` (saida ${hora_fim.slice(0, 5)})` : ''}, entao nao da pra congelar.
          </div>
        )}
      </div>

      <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
        {chamados.map((c) => {
          const linhaBusy = linhaOcupada === c.id;
          const mostrarPicker = transferindoId === c.id;
          return (
            <div
              key={c.id}
              className="p-2.5 rounded-lg"
              style={{ border: '1px solid var(--panel-border, rgba(0,0,0,0.12))', backgroundColor: 'var(--panel-bg, transparent)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {c.contato_nome || 'Sem nome'}
                    {c.inbox === 'compras' && <span className="ml-1.5 text-[11px] opacity-60">· Compras</span>}
                  </div>
                  <div className="text-[12px] opacity-70 truncate">
                    {c.ultima_mensagem_preview || '—'}
                  </div>
                </div>
                <span className="text-[11px] opacity-55 whitespace-nowrap pt-0.5">{tempoAtras(c.ultima_mensagem_em)}</span>
              </div>

              {mostrarPicker ? (
                <div className="mt-2">
                  <div className="text-[11px] uppercase tracking-wide opacity-60 mb-1">Transferir para (em turno)</div>
                  {agentes == null ? (
                    <div className="flex items-center gap-1.5 text-xs opacity-70 py-1"><Loader2 size={13} className="animate-spin" /> carregando...</div>
                  ) : agentes.length === 0 ? (
                    <div className="text-xs opacity-70 py-1">Ninguem em turno pra receber. Encerre o chamado.</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {agentes.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className="px-2.5 py-1 rounded text-xs flex items-center gap-1"
                          style={{ border: '1px solid var(--primary)', color: 'var(--primary)' }}
                          onClick={() => void transferirPara(c.id, a.id)}
                          disabled={linhaBusy}
                        >
                          {linhaBusy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                          {a.nome}
                        </button>
                      ))}
                    </div>
                  )}
                  <button type="button" className="text-[11px] opacity-60 mt-1.5 underline" onClick={() => setTransferindoId(null)} disabled={linhaBusy}>
                    cancelar
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-2">
                  <button
                    type="button"
                    className="px-2.5 py-1 rounded text-xs flex items-center gap-1 hover:bg-black/5"
                    style={{ border: '1px solid var(--panel-border, rgba(0,0,0,0.15))' }}
                    onClick={() => onAbrir(c.id)}
                    disabled={ocupado}
                  >
                    <MessageCircle size={12} /> Abrir
                  </button>
                  <button
                    type="button"
                    className="px-2.5 py-1 rounded text-xs flex items-center gap-1 hover:bg-black/5"
                    style={{ border: '1px solid var(--panel-border, rgba(0,0,0,0.15))' }}
                    onClick={() => setTransferindoId(c.id)}
                    disabled={ocupado}
                  >
                    <Forward size={12} /> Transferir
                  </button>
                  <button
                    type="button"
                    className="px-2.5 py-1 rounded text-xs flex items-center gap-1 hover:bg-black/5"
                    style={{ border: '1px solid var(--panel-border, rgba(0,0,0,0.15))' }}
                    onClick={() => void encerrarUm(c.id)}
                    disabled={ocupado}
                  >
                    {linhaBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Encerrar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
